import { createPortal } from "react-dom";
import { Loader2 } from "lucide-react";

const LOGO = "/assets/images/kiddo-logo.png";

/**
 * Full-screen branded loader. Portalled to <body> so it always centers on
 * the viewport regardless of any ancestor transforms (e.g. the route-fade-in
 * wrapper uses `transform` which would otherwise create a containing block
 * for `position: fixed` children and visually push the loader off-center).
 */
/**
 * Full-screen branded loader. Portalled to <body> so it always centers on
 * the viewport regardless of any ancestor transforms (e.g. the route-fade-in
 * wrapper uses `transform` which would otherwise create a containing block
 * for `position: fixed` children and visually push the loader off-center).
 *
 * Renders whenever mounted (no `visible` gate needed) — callers already
 * control presence via their own `if (loading) return <BrandedLoader/>`
 * early-return, or by passing `visible` explicitly for inline usage
 * alongside other JSX. Accepts `label` as an alias for `title` (most call
 * sites use `label`).
 */
export default function BrandedLoader({ visible = true, title, label, subtitle }) {
  if (!visible || typeof document === "undefined") return null;
  const heading = title || label || "Loading…";
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-cream/95 dark:bg-[#0F0B1A]/95 backdrop-blur-md animate-fade-in"
      role="alertdialog"
      aria-modal="true"
      data-testid="branded-loader"
    >
      <div className="text-center px-6 max-w-sm">
        <div className="relative w-24 h-24 mx-auto mb-6">
          <span className="absolute inset-0 rounded-full bg-teal-600/20 animate-ping" />
          <span className="absolute inset-2 rounded-full bg-teal-600/30 animate-pulse" />
          <Loader2 className="absolute inset-0 m-auto w-24 h-24 text-teal-600/40 animate-spin" strokeWidth={1.5} />
          <img src={LOGO} alt="Kiddo+" className="absolute inset-0 m-auto w-12 h-12 rounded-full shadow-soft" />
        </div>
        <h2 className="font-heading text-xl sm:text-2xl text-inkdark dark:text-cream animate-fade-in-up">
          {heading}
        </h2>
        {subtitle && (
          <p className="text-sm text-inkmid dark:text-inklight mt-2 animate-fade-in-up animation-delay-100">
            {subtitle}
          </p>
        )}
        <div className="flex items-center justify-center gap-1.5 mt-5">
          <span className="w-2 h-2 rounded-full bg-teal-600 animate-bounce" />
          <span className="w-2 h-2 rounded-full bg-teal-600 animate-bounce animation-delay-150" />
          <span className="w-2 h-2 rounded-full bg-teal-600 animate-bounce animation-delay-300" />
        </div>
      </div>
    </div>,
    document.body
  );
}
